/**
 * docs-return — the "Back to Chippie" button on the public /docs pages.
 *
 * Chip AI's help links carry the app page they were opened from in the hash
 * (`/docs/taking-payments#from=/job/12`, built by ai_chat_controller.js). On
 * arrival this controller keeps that path for the tab (sessionStorage), strips
 * the hash, and shows the button, so it stays while the reader moves between
 * help pages. A page reached any other way shows no button.
 *
 * The button is a plain link to the saved page rather than history.back(),
 * so it returns to the app page however many help pages were read in
 * between, the same way in the iOS and Android shells, the installed web
 * app and a browser tab.
 *
 * Template: templates/public_docs/_back_to_app.html
 */
import { Controller } from "@hotwired/stimulus";

const STORAGE_KEY = "chippie.docsReturn";
const HASH_PREFIX = "#from=";

// A same-site app path only: one leading slash (never "//host"), no
// backslashes, and not a help page itself.
function safeAppPath(value) {
  if (!value || !/^\/(?![/\\])[^\s\\]*$/.test(value)) return null;
  return value.startsWith("/docs") ? null : value;
}

function readStored() {
  try {
    return safeAppPath(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function store(path) {
  try {
    if (path) sessionStorage.setItem(STORAGE_KEY, path);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: the button still works for this page via the hash.
  }
}

export default class extends Controller {
  connect() {
    const path = this.#takeFromHash() || readStored();
    if (!path) return;
    this.element.href = path;
    this.element.hidden = false;
  }

  back() {
    store(null);
  }

  #takeFromHash() {
    const { hash } = window.location;
    if (!hash.startsWith(HASH_PREFIX)) return null;
    const path = safeAppPath(decodeURIComponent(hash.slice(HASH_PREFIX.length)));
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState(window.history.state, "", clean);
    store(path);
    return path;
  }
}
